import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FloatingPortal } from '@floating-ui/react';
import { useUserCard } from '@/hooks/useUserCard';
import { UserCard } from '@/components/deck/UserCard';
import './UserProfilePopover.css';

interface Props {
  userId: string;
  username: string;
  children: React.ReactNode;
  onUsernameClick?: (username: string) => void;
}

export function UserProfilePopover({ userId, username, children, onUsernameClick }: Props) {
  const navigate = useNavigate();
  const {
    isOpen,
    profile,
    loading,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    fetchUserCard,
  } = useUserCard();

  // Delay showing popover until position is calculated
  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowPopover(true), 10);
      return () => clearTimeout(timer);
    } else {
      setShowPopover(false);
    }
  }, [isOpen]);

  // Merge handlers
  const referenceProps = getReferenceProps({
    onMouseEnter: () => {
      if (!profile) {
        fetchUserCard(userId);
      }
    },
    onClick: (e: React.MouseEvent) => {
      if (onUsernameClick) {
        e.stopPropagation();
        onUsernameClick(username);
      }
    },
  });

  // Navigate to profile when card is clicked
  const handleCardClick = () => {
    const targetUsername = profile?.username || username;
    navigate(`/u/${targetUsername}`);
  };

  return (
    <>
      {/* Username trigger */}
      <span
        ref={refs.setReference}
        {...referenceProps}
        className="user-profile-trigger clickable"
        style={{ cursor: onUsernameClick ? 'pointer' : 'default' }}
      >
        {children}
      </span>

      {/* Popover with DECK UserCard */}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={`user-profile-popover deck-card-popover ${showPopover ? 'popover-open' : ''}`}
          >
            {loading ? (
              <div className="popover-loading">
                <div className="loading-spinner-small" />
              </div>
            ) : profile ? (
              <UserCard
                profile={profile}
                onClick={handleCardClick}
              />
            ) : (
              <div className="popover-error">Erro ao carregar</div>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
